import React, { useContext, useMemo, useState } from 'react';
import { Box, Button } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { MUITableSorting } from 'components/shared/MUITable';
import { FiltersPanel } from 'components/shared/FiltersPanel';
import { CommonDataContext } from 'contexts/CommonDataContext';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { ProposalsDataResponse } from 'modules/aftermarketMenuConstructor/api/types';
import { ProposalMenuSearchableColumnsEnum } from 'modules/aftermarketMenuConstructor/types';
import { GET_PROPOSAL_MENUS, UPDATE_PROPOSAL_MENU } from 'modules/aftermarketMenuConstructor/api';
import { useAftermarketPermissions } from 'modules/aftermarketMenuConstructor/hooks/useAftermarketPermissions';

import { MenuTemplatesTable, MenuTemplateTableEntity } from '../../tables';

const getSortValue = (entity: MenuTemplateTableEntity, key: string): string | number => {
  if (key === 'createdDateTime' || key === 'modifiedDateTime') {
    return entity[key] ? new Date(entity[key] as string).getTime() : 0;
  }
  return (entity[key] as string)?.toString().toLowerCase() ?? '';
};

export const ListOfMenuTemplates = () => {
  const history = useHistory();

  const { isAftermarketPartner } = useContext(CommonDataContext);
  const { hasWriteAccess, hasReadOnlyAccess } = useAftermarketPermissions();

  const [searchText, setSearchText] = useState('');
  const [sorting, setSorting] = useState<{ key: string; value: 'asc' | 'desc' }>({
    key: 'createdDateTime',
    value: 'desc',
  });

  const { handleOpen: handleOpenConfirmation } = useModal(ModalsKeys.Confirmation);

  const {
    loading,
    data: proposalMenusData,
    refetch: refetchProposalMenus,
  } = useQuery<ProposalsDataResponse>(GET_PROPOSAL_MENUS);

  const [updateProposalMenu] = useMutation(UPDATE_PROPOSAL_MENU);

  const proposalMenus = useMemo(() => proposalMenusData?.proposalMenus ?? [], [proposalMenusData?.proposalMenus]);

  const hasLinkedFromMenus = useMemo(() => proposalMenus.some(pm => !!pm.linkedFromCriteria), [proposalMenus]);

  const filteredMenus = useMemo(() => {
    if (!searchText) return proposalMenus;

    const lowerSearch = searchText.toLowerCase();

    return proposalMenus.filter(menu =>
      Object.values(ProposalMenuSearchableColumnsEnum).some(column => menu[column]?.toLowerCase().includes(lowerSearch))
    );
  }, [proposalMenus, searchText]);

  const tableData: MenuTemplateTableEntity[] = useMemo(
    () =>
      filteredMenus.map(menu => ({
        id: menu.proposalMenuId,
        name: menu.name || '-',
        description: menu.description || '-',
        createdDateTime: menu.createdDateTime || '-',
        canDelete: !menu.linkedFromCriteria ? 1 : 0,
        modifiedDateTime: menu.modifiedDateTime ?? '-',
        sharedFrom: menu.linkedFromCriteria?.account?.name ?? '-',
        dealers:
          menu.linkedToCriterias
            ?.map(c => c.account?.name)
            .filter(Boolean)
            .join(', ') || '-',
      })),
    [filteredMenus]
  );

  const sortedTableData = useMemo(() => {
    return [...tableData].sort((a, b) => {
      const aVal = getSortValue(a, sorting.key);
      const bVal = getSortValue(b, sorting.key);

      if (aVal < bVal) return sorting.value === 'asc' ? -1 : 1;
      if (aVal > bVal) return sorting.value === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tableData, sorting]);

  const handleSortingChange: MUITableSorting['onChange'] = nextValue => {
    setSorting(nextValue);
  };

  const handleEdit = (id: string) => {
    history.push(`/aftermarket/menu/edit/${id}`);
  };

  const handleDelete = (id: string) => {
    handleOpenConfirmation({
      title: 'Confirmation',
      message: 'Are you sure you want to remove Menu?',
      onConfirm: async () => {
        await updateProposalMenu({
          variables: {
            input: { proposalMenuId: id, archived: true },
          },
        });

        await refetchProposalMenus();
      },
    });
  };

  return (
    <RenderWithCondition condition={hasWriteAccess || hasReadOnlyAccess} fallBack={<h3>Access Denied</h3>}>
      <CardContainer
        title="Menu Templates"
        style={{ height: 'calc(100% - 4.2rem)' }}
        actionBtn={
          hasWriteAccess && (
            <Button
              size="small"
              color="primary"
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={() => history.push('/aftermarket/menu/new')}
            >
              Add Template
            </Button>
          )
        }
      >
        <FiltersPanel onSearchChange={(_, nextValue) => setSearchText(nextValue)} />

        <Box sx={{ height: 'calc(100% - 7rem)' }}>
          <MenuTemplatesTable
            isLoading={loading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            tableData={sortedTableData}
            hasWriteAccess={hasWriteAccess}
            hasLinkedFromMenus={hasLinkedFromMenus}
            isAftermarketPartner={isAftermarketPartner}
            sorting={{ ...sorting, onChange: handleSortingChange }}
          />
        </Box>
      </CardContainer>
    </RenderWithCondition>
  );
};
